/**
 * Statblöcke für Kreaturen (Monster, NSC, Charaktere).
 *
 * Datenformat = das `.monster`-JSON des Tetra-Cube-/Statblock5e-Monster-Makers.
 * In der Notiz liegt der Statblock kompakt (ohne Standardwerte) im Frontmatter
 * unter `statblock`, `.monster`-Dateien lassen sich verlustfrei laden und exportieren.
 * Eigene Erweiterung: `format` (`2014` / `2024`) für die Darstellung.
 */

export const ABILITY_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const;
export type AbilityKey = (typeof ABILITY_KEYS)[number];

export interface StatblockEntry {
  name: string;
  desc: string;
}

export interface Statblock {
  name?: string;
  size?: string;
  type?: string;
  tag?: string;
  alignment?: string;
  /** Eigene Erweiterung: Darstellung nach 5e 2014 (Standard) oder 2024. */
  format?: '2014' | '2024';

  hitDice?: number;
  customHP?: boolean;
  hpText?: string;

  armorName?: string;
  shieldBonus?: number;
  natArmorBonus?: number;
  otherArmorDesc?: string;

  speed?: number;
  burrowSpeed?: number;
  climbSpeed?: number;
  flySpeed?: number;
  hover?: boolean;
  swimSpeed?: number;
  customSpeed?: boolean;
  speedDesc?: string;

  strPoints?: number;
  dexPoints?: number;
  conPoints?: number;
  intPoints?: number;
  wisPoints?: number;
  chaPoints?: number;

  blindsight?: number;
  blind?: boolean;
  darkvision?: number;
  tremorsense?: number;
  truesight?: number;
  telepathy?: number;

  cr?: string;
  customCr?: string;
  customProf?: number;

  isLegendary?: boolean;
  legendariesDescription?: string;
  isLair?: boolean;
  lairDescription?: string;
  lairDescriptionEnd?: string;
  isMythic?: boolean;
  mythicDescription?: string;
  isRegional?: boolean;
  regionalDescription?: string;
  regionalDescriptionEnd?: string;

  abilities?: StatblockEntry[];
  actions?: StatblockEntry[];
  bonusActions?: StatblockEntry[];
  reactions?: StatblockEntry[];
  legendaries?: StatblockEntry[];
  mythics?: StatblockEntry[];
  lairs?: StatblockEntry[];
  regionals?: StatblockEntry[];

  sthrows?: { name: AbilityKey; order?: number }[];
  skills?: { name: string; stat: AbilityKey; note?: string }[];
  damagetypes?: { name: string; note?: string; type: 'v' | 'r' | 'i' }[];
  specialdamage?: { name: string; note?: string; type: 'v' | 'r' | 'i' }[];
  conditions?: { name: string }[];
  languages?: { name: string; speaks: boolean }[];
  understandsBut?: string;

  shortName?: string;
  pluralName?: string;
  doubleColumns?: boolean;
  separationPoint?: number;
}

const ENTRY_LISTS = ['abilities', 'actions', 'bonusActions', 'reactions', 'legendaries', 'mythics', 'lairs', 'regionals'] as const;
export type StatblockEntryList = (typeof ENTRY_LISTS)[number];

const NUM_KEYS = [
  'hitDice',
  'shieldBonus',
  'natArmorBonus',
  'speed',
  'burrowSpeed',
  'climbSpeed',
  'flySpeed',
  'swimSpeed',
  'strPoints',
  'dexPoints',
  'conPoints',
  'intPoints',
  'wisPoints',
  'chaPoints',
  'blindsight',
  'darkvision',
  'tremorsense',
  'truesight',
  'telepathy',
  'customProf',
  'separationPoint',
] as const;
const BOOL_KEYS = ['customHP', 'hover', 'customSpeed', 'blind', 'isLegendary', 'isLair', 'isMythic', 'isRegional', 'doubleColumns'] as const;
const STR_KEYS = [
  'name',
  'size',
  'type',
  'tag',
  'alignment',
  'hpText',
  'armorName',
  'otherArmorDesc',
  'speedDesc',
  'cr',
  'customCr',
  'legendariesDescription',
  'lairDescription',
  'lairDescriptionEnd',
  'mythicDescription',
  'regionalDescription',
  'regionalDescriptionEnd',
  'understandsBut',
  'shortName',
  'pluralName',
] as const;

export const SIZES = ['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan'] as const;
const HIT_DIE: Record<string, number> = { tiny: 4, small: 6, medium: 8, large: 10, huge: 12, gargantuan: 20 };

export const SKILLS: readonly { name: string; stat: AbilityKey }[] = [
  { name: 'acrobatics', stat: 'dex' },
  { name: 'animal handling', stat: 'wis' },
  { name: 'arcana', stat: 'int' },
  { name: 'athletics', stat: 'str' },
  { name: 'deception', stat: 'cha' },
  { name: 'history', stat: 'int' },
  { name: 'insight', stat: 'wis' },
  { name: 'intimidation', stat: 'cha' },
  { name: 'investigation', stat: 'int' },
  { name: 'medicine', stat: 'wis' },
  { name: 'nature', stat: 'int' },
  { name: 'perception', stat: 'wis' },
  { name: 'performance', stat: 'cha' },
  { name: 'persuasion', stat: 'cha' },
  { name: 'religion', stat: 'int' },
  { name: 'sleight of hand', stat: 'dex' },
  { name: 'stealth', stat: 'dex' },
  { name: 'survival', stat: 'wis' },
];

export const DAMAGE_TYPES = [
  'acid',
  'bludgeoning',
  'cold',
  'fire',
  'force',
  'lightning',
  'necrotic',
  'piercing',
  'poison',
  'psychic',
  'radiant',
  'slashing',
  'thunder',
] as const;

export const CONDITIONS = [
  'blinded',
  'charmed',
  'deafened',
  'exhaustion',
  'frightened',
  'grappled',
  'incapacitated',
  'invisible',
  'paralyzed',
  'petrified',
  'poisoned',
  'prone',
  'restrained',
  'stunned',
  'unconscious',
] as const;

/** Rüstungen wie im Monster-Maker: Basis-RK und maximaler GES-Bonus (null = unbegrenzt). */
export const ARMORS: Record<string, { base: number; maxDex: number | null }> = {
  none: { base: 10, maxDex: null },
  'natural armor': { base: 10, maxDex: null },
  'mage armor': { base: 10, maxDex: null },
  'padded armor': { base: 11, maxDex: null },
  'leather armor': { base: 11, maxDex: null },
  'studded leather': { base: 12, maxDex: null },
  'hide armor': { base: 12, maxDex: 2 },
  'chain shirt': { base: 13, maxDex: 2 },
  'scale mail': { base: 14, maxDex: 2 },
  breastplate: { base: 14, maxDex: 2 },
  'half plate': { base: 15, maxDex: 2 },
  'ring mail': { base: 14, maxDex: 0 },
  'chain mail': { base: 16, maxDex: 0 },
  splint: { base: 17, maxDex: 0 },
  plate: { base: 18, maxDex: 0 },
  other: { base: 10, maxDex: null },
};

export const CR_TABLE: Record<string, { xp: number; prof: number }> = {
  '0': { xp: 10, prof: 2 },
  '1/8': { xp: 25, prof: 2 },
  '1/4': { xp: 50, prof: 2 },
  '1/2': { xp: 100, prof: 2 },
  '1': { xp: 200, prof: 2 },
  '2': { xp: 450, prof: 2 },
  '3': { xp: 700, prof: 2 },
  '4': { xp: 1100, prof: 2 },
  '5': { xp: 1800, prof: 3 },
  '6': { xp: 2300, prof: 3 },
  '7': { xp: 2900, prof: 3 },
  '8': { xp: 3900, prof: 3 },
  '9': { xp: 5000, prof: 4 },
  '10': { xp: 5900, prof: 4 },
  '11': { xp: 7200, prof: 4 },
  '12': { xp: 8400, prof: 4 },
  '13': { xp: 10000, prof: 5 },
  '14': { xp: 11500, prof: 5 },
  '15': { xp: 13000, prof: 5 },
  '16': { xp: 15000, prof: 5 },
  '17': { xp: 18000, prof: 6 },
  '18': { xp: 20000, prof: 6 },
  '19': { xp: 22000, prof: 6 },
  '20': { xp: 25000, prof: 6 },
  '21': { xp: 33000, prof: 7 },
  '22': { xp: 41000, prof: 7 },
  '23': { xp: 50000, prof: 7 },
  '24': { xp: 62000, prof: 7 },
  '25': { xp: 75000, prof: 8 },
  '26': { xp: 90000, prof: 8 },
  '27': { xp: 105000, prof: 8 },
  '28': { xp: 120000, prof: 8 },
  '29': { xp: 135000, prof: 9 },
  '30': { xp: 155000, prof: 9 },
};

export function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

export function formatMod(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

export function abilityScore(sb: Statblock, key: AbilityKey): number {
  const v = sb[`${key}Points`];
  return typeof v === 'number' && Number.isFinite(v) ? v : 10;
}

/** Standard-Statblock: Gemeiner Bürger (alle Werte 10, 4 TP, RK 10). */
export function commonerStatblock(name = ''): Statblock {
  return {
    name,
    size: 'medium',
    type: 'Humanoid',
    alignment: 'any alignment',
    hitDice: 1,
    armorName: 'none',
    speed: 30,
    strPoints: 10,
    dexPoints: 10,
    conPoints: 10,
    intPoints: 10,
    wisPoints: 10,
    chaPoints: 10,
    cr: '0',
  };
}

// ---------- Normalisierung ----------

function toNum(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function entries(v: unknown): StatblockEntry[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map((e) => ({ name: String(e.name ?? ''), desc: String(e.desc ?? '') }))
    .filter((e) => e.name || e.desc);
}

const isAbility = (s: unknown): s is AbilityKey => typeof s === 'string' && (ABILITY_KEYS as readonly string[]).includes(s);
const isDmgType = (s: unknown): s is 'v' | 'r' | 'i' => s === 'v' || s === 'r' || s === 'i';

/**
 * Liest einen Statblock aus beliebigem JSON (`.monster`-Datei oder Frontmatter)
 * und liefert die kompakte Form: nur bekannte Felder, ohne Standard-/Leerwerte.
 */
export function normalizeStatblock(raw: unknown): Statblock {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: Statblock = {};
  const o = out as Record<string, unknown>;
  for (const k of STR_KEYS) if (typeof r[k] === 'string' || typeof r[k] === 'number') o[k] = String(r[k]);
  for (const k of NUM_KEYS) {
    const n = toNum(r[k]);
    if (n !== undefined) o[k] = n;
  }
  for (const k of BOOL_KEYS) if (r[k] === true) o[k] = true;
  if (r.format === '2024' || r.format === 2024) out.format = '2024';
  for (const k of ENTRY_LISTS) {
    const list = entries(r[k]);
    if (list.length) out[k] = list;
  }
  if (Array.isArray(r.sthrows)) {
    const st = r.sthrows
      .map((s) => (s && typeof s === 'object' ? (s as Record<string, unknown>).name : s))
      .filter(isAbility);
    const uniq = ABILITY_KEYS.filter((a) => st.includes(a));
    if (uniq.length) out.sthrows = uniq.map((name) => ({ name, order: ABILITY_KEYS.indexOf(name) }));
  }
  if (Array.isArray(r.skills)) {
    const sk = r.skills
      .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object' && typeof s.name === 'string')
      .map((s) => {
        const name = String(s.name).toLowerCase();
        const stat = isAbility(s.stat) ? s.stat : (SKILLS.find((x) => x.name === name)?.stat ?? 'wis');
        const expertise = s.expertise === true || s.isExpertise === true || /ex/i.test(String(s.note ?? ''));
        return expertise ? { name, stat, note: ' (ex)' } : { name, stat };
      });
    if (sk.length) out.skills = sk;
  }
  for (const k of ['damagetypes', 'specialdamage'] as const) {
    if (!Array.isArray(r[k])) continue;
    const list = (r[k] as unknown[])
      .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
      .filter((s) => typeof s.name === 'string' && isDmgType(s.type))
      .map((s) => ({ name: String(s.name), type: s.type as 'v' | 'r' | 'i' }));
    if (list.length) out[k] = list;
  }
  if (Array.isArray(r.conditions)) {
    const list = r.conditions
      .map((c) => (c && typeof c === 'object' ? (c as Record<string, unknown>).name : c))
      .filter((n): n is string => typeof n === 'string' && n.trim() !== '')
      .map((name) => ({ name }));
    if (list.length) out.conditions = list;
  }
  if (Array.isArray(r.languages)) {
    const list = r.languages
      .filter((l): l is Record<string, unknown> => !!l && typeof l === 'object' && typeof l.name === 'string')
      .map((l) => ({ name: String(l.name), speaks: l.speaks !== false }));
    if (list.length) out.languages = list;
  }

  // Werte entfernen, die nur in bestimmten Modi gelten (Monster-Maker speichert sie immer mit)
  if (!out.customHP) delete out.hpText;
  if (!out.customSpeed) delete out.speedDesc;
  if (out.armorName !== 'other') delete out.otherArmorDesc;
  if (out.armorName !== 'natural armor') delete out.natArmorBonus;
  if (out.cr !== '*') delete out.customCr;
  // Übungsbonus gilt bei eigenem HG und ohne HG (Spielercharaktere: aus der Stufe)
  if (out.cr && out.cr !== '*') delete out.customProf;
  if (!out.isLegendary) delete out.legendariesDescription;
  if (!out.isLair) {
    delete out.lairDescription;
    delete out.lairDescriptionEnd;
  }
  if (!out.isMythic) delete out.mythicDescription;
  if (!out.isRegional) {
    delete out.regionalDescription;
    delete out.regionalDescriptionEnd;
  }
  for (const k of ['shieldBonus', 'burrowSpeed', 'climbSpeed', 'flySpeed', 'swimSpeed', 'blindsight', 'darkvision', 'tremorsense', 'truesight', 'telepathy'] as const) {
    if (out[k] === 0) delete out[k];
  }
  if (!out.flySpeed) delete out.hover;
  if (!out.blindsight) delete out.blind;
  // Nur aus Leerzeichen bestehende Zusätze entfernen (sonst „Humanoid ( )“)
  for (const k of ['tag', 'understandsBut', 'shortName', 'pluralName'] as const) if (out[k]?.trim() === '') delete out[k];
  if (out.separationPoint !== undefined && !out.doubleColumns) delete out.separationPoint;
  return out;
}

/** Erzeugt eine vollständige `.monster`-Datei (alle Felder des Monster-Makers). */
export function toMonsterFile(sb: Statblock): Record<string, unknown> {
  const name = sb.name || 'Monster';
  const n = name.toLowerCase();
  const prof = proficiency(sb);
  const full: Record<string, unknown> = {
    name,
    size: sb.size ?? 'medium',
    type: sb.type ?? 'Humanoid',
    tag: sb.tag ?? '',
    alignment: sb.alignment ?? '',
    hitDice: sb.hitDice ?? 1,
    armorName: sb.armorName ?? 'none',
    shieldBonus: sb.shieldBonus ?? 0,
    natArmorBonus: sb.natArmorBonus ?? 0,
    otherArmorDesc: sb.otherArmorDesc ?? String(computeAc(sb).ac),
    speed: sb.speed ?? 30,
    burrowSpeed: sb.burrowSpeed ?? 0,
    climbSpeed: sb.climbSpeed ?? 0,
    flySpeed: sb.flySpeed ?? 0,
    hover: sb.hover ?? false,
    swimSpeed: sb.swimSpeed ?? 0,
    customHP: sb.customHP ?? false,
    customSpeed: sb.customSpeed ?? false,
    hpText: sb.hpText ?? String(computeHp(sb).hp),
    speedDesc: sb.speedDesc ?? `${sb.speed ?? 30} ft.`,
    strPoints: abilityScore(sb, 'str'),
    dexPoints: abilityScore(sb, 'dex'),
    conPoints: abilityScore(sb, 'con'),
    intPoints: abilityScore(sb, 'int'),
    wisPoints: abilityScore(sb, 'wis'),
    chaPoints: abilityScore(sb, 'cha'),
    blindsight: sb.blindsight ?? 0,
    blind: sb.blind ?? false,
    darkvision: sb.darkvision ?? 0,
    tremorsense: sb.tremorsense ?? 0,
    truesight: sb.truesight ?? 0,
    telepathy: sb.telepathy ?? 0,
    cr: sb.cr ?? '0',
    customCr: sb.customCr ?? crText(sb),
    customProf: sb.customProf ?? prof,
    isLegendary: sb.isLegendary ?? false,
    legendariesDescription:
      sb.legendariesDescription ??
      `The ${n} can take 3 legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. The ${n} regains spent legendary actions at the start of its turn.`,
    isLair: sb.isLair ?? false,
    lairDescription:
      sb.lairDescription ??
      `When fighting inside its lair, the ${n} can invoke the ambient magic to take lair actions. On initiative count 20 (losing initiative ties), the ${n} can take one lair action to cause one of the following effects:`,
    lairDescriptionEnd:
      sb.lairDescriptionEnd ?? `The ${n} can't repeat an effect until they have all been used, and it can't use the same effect two rounds in a row.`,
    isMythic: sb.isMythic ?? false,
    mythicDescription:
      sb.mythicDescription ?? `If the ${n}'s mythic trait is active, it can use the options below as legendary actions for 1 hour after using {Some Ability}.`,
    isRegional: sb.isRegional ?? false,
    regionalDescription:
      sb.regionalDescription ?? `The region containing the ${n}'s lair is warped by the creature's presence, which creates one or more of the following effects:`,
    regionalDescriptionEnd: sb.regionalDescriptionEnd ?? `If the ${n} dies, the first two effects fade over the course of 3d10 days.`,
    properties: [],
    abilities: sb.abilities ?? [],
    actions: sb.actions ?? [],
    bonusActions: sb.bonusActions ?? [],
    reactions: sb.reactions ?? [],
    legendaries: sb.legendaries ?? [],
    mythics: sb.mythics ?? [],
    lairs: sb.lairs ?? [],
    regionals: sb.regionals ?? [],
    sthrows: sb.sthrows ?? [],
    skills: sb.skills ?? [],
    damagetypes: (sb.damagetypes ?? []).map((d) => ({ ...d, note: DMG_NOTE[d.type] })),
    specialdamage: (sb.specialdamage ?? []).map((d) => ({ ...d, note: DMG_NOTE[d.type] })),
    conditions: sb.conditions ?? [],
    languages: sb.languages ?? [],
    understandsBut: sb.understandsBut ?? '',
    shortName: sb.shortName ?? '',
    pluralName: sb.pluralName ?? '',
    doubleColumns: sb.doubleColumns ?? false,
    separationPoint: sb.separationPoint ?? 1,
    damage: [],
  };
  if (sb.format) full.format = sb.format;
  return full;
}

const DMG_NOTE = { v: ' (Vulnerable)', r: ' (Resistant)', i: ' (Immune)' } as const;

// ---------- Berechnete Werte ----------

export function proficiency(sb: Statblock): number {
  if (sb.cr === '*' || !sb.cr) return sb.customProf ?? 2;
  return CR_TABLE[sb.cr]?.prof ?? 2;
}

export function crText(sb: Statblock, xpLabel = 'XP'): string {
  if (sb.cr === '*') return sb.customCr ?? '';
  if (!sb.cr) return '';
  const row = CR_TABLE[sb.cr];
  return row ? `${sb.cr} (${row.xp.toLocaleString('en-US')} ${xpLabel})` : sb.cr;
}

export function computeAc(sb: Statblock): { ac: number; armor: string | null; shield: boolean; mageArmorAc: number | null } {
  const dex = abilityMod(abilityScore(sb, 'dex'));
  const name = sb.armorName ?? 'none';
  const shield = (sb.shieldBonus ?? 0) > 0 ? (sb.shieldBonus ?? 0) : 0;
  if (name === 'other') {
    const m = /\d+/.exec(sb.otherArmorDesc ?? '');
    return { ac: m ? Number(m[0]) : 10 + dex, armor: null, shield: false, mageArmorAc: null };
  }
  const a = ARMORS[name] ?? ARMORS.none!;
  const dexPart = a.maxDex === null ? dex : Math.min(dex, a.maxDex);
  let ac = a.base + dexPart + shield;
  if (name === 'natural armor') ac += sb.natArmorBonus ?? 0;
  if (name === 'mage armor') return { ac, armor: null, shield: shield > 0, mageArmorAc: 13 + dex + shield };
  return { ac, armor: name === 'none' ? null : name, shield: shield > 0, mageArmorAc: null };
}

export function computeHp(sb: Statblock): { hp: number; formula: string | null } {
  if (sb.customHP) {
    const m = /\d+/.exec(sb.hpText ?? '');
    const f = /\d+\s*[dDwW]\s*\d+(\s*[+-−]\s*\d+)?/.exec(sb.hpText ?? '');
    return { hp: m ? Number(m[0]) : 1, formula: f ? f[0].replace(/\s+/g, '').replace(/[wW]/, 'd').replace('−', '-') : null };
  }
  const n = Math.max(1, Math.floor(sb.hitDice ?? 1));
  const die = HIT_DIE[(sb.size ?? 'medium').toLowerCase()] ?? 8;
  const con = abilityMod(abilityScore(sb, 'con'));
  const hp = Math.max(1, Math.floor((n * (die + 1)) / 2) + n * con);
  const bonus = n * con;
  return { hp, formula: `${n}d${die}${bonus === 0 ? '' : bonus > 0 ? ` + ${bonus}` : ` - ${-bonus}`}` };
}

export function hasSaveProficiency(sb: Statblock, key: AbilityKey): boolean {
  return (sb.sthrows ?? []).some((s) => s.name === key);
}

export function saveBonus(sb: Statblock, key: AbilityKey): number {
  return abilityMod(abilityScore(sb, key)) + (hasSaveProficiency(sb, key) ? proficiency(sb) : 0);
}

export function skillBonus(sb: Statblock, skill: { name: string; stat: AbilityKey; note?: string }): number {
  const expertise = /ex/i.test(skill.note ?? '');
  return abilityMod(abilityScore(sb, skill.stat)) + proficiency(sb) * (expertise ? 2 : 1);
}

export function passivePerception(sb: Statblock): number {
  const p = (sb.skills ?? []).find((s) => s.name.toLowerCase() === 'perception');
  return 10 + (p ? skillBonus(sb, p) : abilityMod(abilityScore(sb, 'wis')));
}

export function initiativeBonus(sb: Statblock): number {
  return abilityMod(abilityScore(sb, 'dex'));
}

/** Kurzwerte für Kampf-Tracker und Vorschauen. */
export interface StatblockSummary {
  ac: number;
  hp: number;
  hpFormula: string | null;
  initiative: number;
  cr: string | null;
  xp: number | null;
  passivePerception: number;
}

export function summarizeStatblock(sb: Statblock): StatblockSummary {
  const hp = computeHp(sb);
  return {
    ac: computeAc(sb).ac,
    hp: hp.hp,
    hpFormula: hp.formula,
    initiative: initiativeBonus(sb),
    cr: sb.cr === '*' ? (sb.customCr ?? null) : (sb.cr ?? null),
    xp: sb.cr && sb.cr !== '*' ? (CR_TABLE[sb.cr]?.xp ?? null) : null,
    passivePerception: passivePerception(sb),
  };
}

/** Liest den Statblock einer Notiz aus dem Frontmatter (oder null). */
export function statblockOf(frontmatter: Record<string, unknown> | null | undefined): Statblock | null {
  const raw = frontmatter?.statblock;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return normalizeStatblock(raw);
}
